import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { sentenceCase } from 'change-case'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { type CardTransactionPayload } from '@ankor-io/common/types/transaction/transaction'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDate } from '@ankor-io/common/util/dates'
import { getCardTransactionStatusColour } from '@ankor-io/common/util/enum-to-string/transaction'

import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import { getCardTransactionQueryParams } from '@/utils/getQueryParams/getTransactionQueryParams'

const TRANSACTIONS_PER_PAGE = 100
const SKIP = 0

type CardTransactionsProps = {
  cardId: string
}

type CardTransactionFilters = {
  dateFrom: string
  dateTo: string
}

export const CardTransactions = ({ cardId }: CardTransactionsProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isUpdatingFilters, setIsUpdatingFilters] = useState(false)
  const [transactions, setTransactions] = useState<CardTransactionPayload[]>([])
  const [transactionFilters, setTransactionFilters] = useState<CardTransactionFilters>({
    dateFrom: '',
    dateTo: '',
  })

  useEffect(() => {
    if (!cardId || !activeOrganizationId || !activeConnectedAccountId) {
      setIsLoading(false)
      return
    }

    const fetchCardTransactions = async () => {
      try {
        if (!isUpdatingFilters) {
          setIsLoading(true)
        }

        const queryParams = getCardTransactionQueryParams(TRANSACTIONS_PER_PAGE, SKIP, transactionFilters)
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/search?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to fetch card transactions: ${res.status}`)
        }

        const data = (await res.json()) as CardTransactionPayload[]
        setTransactions(data)
      } catch (error) {
        console.error('Error fetching card transactions:', error)
        toast.error('Error fetching card transactions')
      } finally {
        setIsLoading(false)
      }
    }

    fetchCardTransactions()
    // Don't want to re-fetch transactions when isUpdatingFilters flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, activeOrganizationId, activeConnectedAccountId, getAccessToken, transactionFilters])

  /**
   * Handles filter updates with debouncing to prevent excessive API calls.
   * Sets a flag to suppress loading states during rapid filter changes.
   */
  const handleUpdatedFilters = (filters: CardTransactionFilters) => {
    setIsUpdatingFilters(true)
    setTransactionFilters(filters)
    setTimeout(() => {
      setIsUpdatingFilters(false)
    }, 1000)
  }

  // Clear all filters
  const clearFilters = () => {
    setTransactionFilters({
      dateFrom: '',
      dateTo: '',
    })
  }

  return (
    <div className="space-y-6">
      {/* Title and Clear Button */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Heading
            level={3}
            className="text-lg"
          >
            Filters
          </Heading>
          <button
            type="button"
            onClick={clearFilters}
            className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
          >
            Clear filters
          </button>
        </div>

        {/* Card Transaction Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-2">
          <Input
            type="date"
            placeholder="Date From"
            aria-label="Date From"
            value={transactionFilters.dateFrom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdatedFilters({ ...transactionFilters, dateFrom: e.target.value })
            }
          />
          <Input
            type="date"
            placeholder="Date To"
            aria-label="Date To"
            value={transactionFilters.dateTo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdatedFilters({ ...transactionFilters, dateTo: e.target.value })
            }
          />
        </div>
      </div>

      {/* Custom Card Transactions Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table className="max-w-[calc(100vw-3rem)] lg:max-w-[calc(100vw-21rem)]">
            <TableHead>
              <TableRow>
                <TableHeader>Date</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Amount</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow
                  className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  key={transaction.transactionId}
                  to={`/transactions/${transaction.transactionId}`}
                >
                  <TableCell>{formatDate(transaction.awxCardActivityEvent.transactionDate)}</TableCell>
                  <TableCell>
                    {transaction.awxCardActivityEvent.merchantName}
                    <p>
                      {transaction.awxCardActivityEvent.merchantCity},{' '}
                      {transaction.awxCardActivityEvent.merchantCountry}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge color={getCardTransactionStatusColour(transaction.awxCardActivityEvent.status)}>
                      {sentenceCase(transaction.awxCardActivityEvent.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                    {formatCurrency(
                      fromCents(transaction.awxCardActivityEvent.transactionAmountCents),
                      transaction.awxCardActivityEvent.transactionCurrencyCode,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Empty State */}
      {transactions.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="size-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <div className="size-8 bg-primary-500 rounded-full relative">
              <div className="absolute -top-1 -right-1 size-4 bg-white rounded-full border-2 border-primary-500"></div>
            </div>
          </div>
          <h3 className="text-lg font-medium mb-2">No card transactions</h3>
          <p className="text-gray-500 dark:text-gray-400">Card transactions will show up here.</p>
        </div>
      )}
    </div>
  )
}

export default CardTransactions
